// src/slices/inpatientchartSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { setLoading } from "./LoadingSlice";
import { toast } from "react-toastify";
import BaseUrl from '../../BaseUrl';

const authurl = BaseUrl.authurl
const laafihmsurl =BaseUrl.hmsurl
const medicinemasterurl = BaseUrl.pharmacyurl

// Async thunk for fetching medicine master list
export const fetchMedicineMaster = createAsyncThunk(
  "inpatient/fetchMedicineMaster",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.get(`${medicinemasterurl}MedicineMasters`, {
        headers: {
          'accept': 'text/plain'
        }
      });
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      console.error("Failed to fetch medicine master:", error);
      toast.error("Failed to load medicine list.");
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

// Async thunk for fetching patient search results
export const fetchPatientsBySearch = createAsyncThunk(
  'inpatient/fetchPatientsBySearch',
  async (searchTerm, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.get(`${authurl}Auth/GetPatients?id=${searchTerm}`);
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch patients");
      return rejectWithValue(error.response.data);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Async thunk for fetching all admissions
export const fetchAllAdmissions = createAsyncThunk(
  'inpatient/fetchAllAdmissions',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.get(`${laafihmsurl}Admissions`);
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch admissions");
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Async thunk for fetching a specific patient's chart
export const fetchInpatientChartsByMrn = createAsyncThunk(
  'inpatient/fetchInpatientChartsByMrn',
  async (mrn, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.get(`${laafihmsurl}InpatientCharts/GetChartsByMRN?mrn=${mrn}`);
      console.log(response.data,'charts')
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch patient charts");
      return rejectWithValue(error.response.data);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Async thunk for saving inpatient chart
export const saveInpatientChart = createAsyncThunk(
  'inpatient/saveInpatientChart',
  async (chartData, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.post(`${laafihmsurl}InpatientCharts`, chartData);
      toast.success("Patient chart saved successfully!");
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to save patient chart.";
      toast.error(errorMessage);
      console.error("Save chart error:", error);
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

const inpatientchartSlice = createSlice({
  name: 'inpatientchart',
  initialState: {
    patients: [],
    medicineMaster: [],
    inpatientChart: {
      admission: null,
      admissions: [],
      chartlist: [],
      prescriptions: [],
      patientVitals: {
        bloodPressureSystolic: 0,
        bloodPressureDiastolic: 0,
        heartRate: 0,
        temperature: 0,
        respiratoryRate: 0,
        oxygenSaturation: 0,
        weight: 0,
        height: 0
      }
    },
    loading: false,
    error: null,
  },
  reducers: {
    updateVitals: (state, action) => {
      state.inpatientChart.patientVitals = { ...state.inpatientChart.patientVitals, ...action.payload };
    },
    addPrescription: (state, action) => {
      state.inpatientChart.prescriptions.push(action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    clearPrescriptions: (state) => {
      state.inpatientChart.prescriptions = [];
    },
    updatePrescription: (state, action) => {
      const { index, data } = action.payload;
      if (state.inpatientChart.prescriptions[index]) {
        state.inpatientChart.prescriptions[index] = { ...state.inpatientChart.prescriptions[index], ...data };
      }
    },
    removePrescription: (state, action) => {
      state.inpatientChart.prescriptions.splice(action.payload, 1);
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch medicine master
      .addCase(fetchMedicineMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedicineMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.medicineMaster = action.payload;
      })
      .addCase(fetchMedicineMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch patients
      .addCase(fetchPatientsBySearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientsBySearch.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = action.payload;
      })
      .addCase(fetchPatientsBySearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch all admissions
      .addCase(fetchAllAdmissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAdmissions.fulfilled, (state, action) => {
        state.loading = false;
        state.inpatientChart.admissions = action.payload;
      })
      .addCase(fetchAllAdmissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch inpatient charts by MRN
      .addCase(fetchInpatientChartsByMrn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInpatientChartsByMrn.fulfilled, (state, action) => {
        state.loading = false;
        state.inpatientChart = {
          admission: action.payload.admission || null,
          admissions: state.inpatientChart.admissions,
          chartlist: action.payload.chartlist || [],
          prescriptions: action.payload.prescriptions || [],
          patientVitals: action.payload.patientVitals || {
            bloodPressureSystolic: 0,
            bloodPressureDiastolic: 0,
            heartRate: 0,
            temperature: 0,
            respiratoryRate: 0,
            oxygenSaturation: 0,
            weight: 0,
            height: 0
          }
        };
      })
      .addCase(fetchInpatientChartsByMrn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save inpatient chart
      .addCase(saveInpatientChart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveInpatientChart.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new chart to chartlist
        if (action.payload) {
          // Create a new chart object that matches the expected structure
          const newChart = {
            mrn: action.payload.mrn,
            admissionID: action.payload.admissionID,
            chartTimestamp: action.payload.chartTimestamp,
            prescriptions: action.payload.prescriptions,
            patientVitals: action.payload.patientVitals,
            consultedBy: action.payload.consultedBy,
            comments: action.payload.comments
          };
          state.inpatientChart.chartlist = [newChart, ...(state.inpatientChart.chartlist || [])];
        }
        // Clear prescriptions after successful save
        state.inpatientChart.prescriptions = [];
      })
      .addCase(saveInpatientChart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  updateVitals, 
  addPrescription, 
  clearError, 
  clearPrescriptions, 
  updatePrescription, 
  removePrescription 
} = inpatientchartSlice.actions;
export default inpatientchartSlice.reducer;